import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, Package } from 'lucide-react';
import { ProductInfoPanel } from '@/components/products/ProductInfoPanel';

export type MovementType = 'IN' | 'OUT' | 'ADJUST';

const dateLocale = (lng: string) => lng === 'ru' ? 'ru-RU' : lng === 'en' ? 'en-US' : 'uz-UZ';

const TYPE_COLOR: Record<string, string> = {
  IN: 'bg-success/10 text-success', OUT: 'bg-destructive/10 text-destructive',
  TRANSFER: 'bg-info/10 text-info', ADJUST: 'bg-warning/10 text-warning',
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Mahsulot tanlash uchun (yangi harakat rejimi) */
  products: any[];
  /** Tanlangan mahsulot (mavjud qoldiq rejimi) */
  product?: any | null;
  currentQty?: number | null;
  /** Shu mahsulotning harakatlari */
  movements?: any[];
  saving?: boolean;
  onSubmit: (params: { product_id: string; movement_type: MovementType; quantity: number; reference?: string }) => Promise<void> | void;
  onZeroOut?: () => Promise<void> | void;
}

export function StockDialog({
  open, onOpenChange, products, product = null, currentQty = null,
  movements = [], saving, onSubmit, onZeroOut,
}: Props) {
  const { t, i18n } = useTranslation();
  const TYPE_LABEL: Record<string, string> = {
    IN: t('cat_common.in'), OUT: t('cat_common.out'), TRANSFER: t('cat_common.transfer'), ADJUST: t('cat_common.adjust'),
  };
  const isExisting = !!product;
  const [pickedId, setPickedId] = useState('');
  const [search, setSearch] = useState('');
  const [movementType, setMovementType] = useState<MovementType>('IN');
  const [quantity, setQuantity] = useState('');
  const [reference, setReference] = useState('');
  const [confirmZero, setConfirmZero] = useState(false);

  useEffect(() => {
    if (open) {
      setPickedId('');
      setSearch('');
      setMovementType('IN');
      setQuantity('');
      setReference('');
      setConfirmZero(false);
    }
  }, [open]);

  const activeProduct = isExisting
    ? product
    : products.find(p => p.id === pickedId) ?? null;

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? products.filter(p =>
          p.name?.toLowerCase().includes(q) ||
          p.sku?.toLowerCase().includes(q) ||
          p.barcode?.toLowerCase().includes(q))
      : products;
    return list.slice(0, 50);
  }, [products, search]);

  const qtyNum = Number(quantity);
  const canSave = !!activeProduct && quantity !== '' && !isNaN(qtyNum)
    && (movementType === 'ADJUST' ? qtyNum >= 0 : qtyNum > 0);

  const stockStatus = currentQty === null ? null
    : Number(currentQty) === 0 ? { label: t('cat_common.stockOut'), cls: 'bg-destructive/10 text-destructive' }
    : Number(currentQty) < 10 ? { label: t('cat_common.stockLow'), cls: 'bg-warning/10 text-warning' }
    : { label: t('cat_common.stockOk'), cls: 'bg-success/10 text-success' };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none w-screen h-[100dvh] max-h-[100dvh] top-0 left-0 translate-x-0 translate-y-0 rounded-none p-0 gap-0 flex flex-col overflow-hidden">
        <div className="flex flex-col h-full min-h-0 max-w-6xl mx-auto w-full">
          <DialogHeader className="shrink-0 px-6 py-5 border-b text-left">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Package size={18} />
              {isExisting ? (activeProduct?.name || t('cat_inventory.stockItem')) : t('cat_inventory.movement')}
            </DialogTitle>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              {activeProduct?.sku && (
                <span className="text-sm text-muted-foreground font-mono">{activeProduct.sku}</span>
              )}
              {currentQty !== null && (
                <>
                  <span className="text-2xl font-semibold text-foreground">{currentQty}</span>
                  <span className="text-sm text-muted-foreground">{activeProduct?.unit}</span>
                  {stockStatus && <Badge className={`text-xs border-0 ${stockStatus.cls}`}>{stockStatus.label}</Badge>}
                </>
              )}
            </div>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-6">
            {!isExisting && (
              <div className="rounded-xl border border-border bg-card">
                <div className="px-4 py-3 border-b border-border">
                  <h3 className="text-sm font-semibold text-foreground">{t('cat_inventory.pickProduct')}</h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="relative max-w-md">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t('cat_inventory.searchPlaceholder')}
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="pl-9 h-11"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                    {filteredProducts.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground">{t('cat_common.productNotFound')}</p>
                    ) : filteredProducts.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPickedId(p.id)}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${pickedId === p.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50 text-foreground'}`}
                      >
                        <span className="font-medium">{p.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground font-mono">{p.sku}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeProduct && <ProductInfoPanel product={activeProduct} />}

            <div className="rounded-xl border border-border bg-card">
              <div className="px-4 py-3 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">{t('cat_inventory.operation')}</h3>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>{t('cat_inventory.movementType')}</Label>
                  <Select value={movementType} onValueChange={v => setMovementType(v as MovementType)}>
                    <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="IN">{t('cat_common.in')}</SelectItem>
                      <SelectItem value="OUT">{t('cat_common.out')}</SelectItem>
                      <SelectItem value="ADJUST">{t('cat_inventory.adjustNew')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="qty">{t('cat_common.quantity')}</Label>
                  <Input
                    id="qty"
                    type="number"
                    min={movementType === 'ADJUST' ? 0 : 1}
                    value={quantity}
                    onChange={e => setQuantity(e.target.value)}
                    className="h-11 text-base"
                    placeholder={movementType === 'ADJUST' ? t('cat_inventory.newStock') : t('cat_common.quantity')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ref">{t('cat_inventory.noteOptional')}</Label>
                  <Input
                    id="ref"
                    value={reference}
                    onChange={e => setReference(e.target.value)}
                    className="h-11"
                    placeholder={t('cat_inventory.noteExample')}
                  />
                </div>
              </div>
            </div>

            {isExisting && (
              <div className="rounded-xl border border-border bg-card">
                <div className="px-4 py-3 border-b border-border">
                  <h3 className="text-sm font-semibold text-foreground">{t('cat_inventory.recentMovements')}</h3>
                </div>
                {movements.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">{t('cat_inventory.noMovements')}</p>
                ) : (
                  <div className="divide-y divide-border">
                    {movements.slice(0, 20).map((m: any) => (
                      <div key={m.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                        <Badge className={`text-xs border-0 ${TYPE_COLOR[m.movement_type] || ''}`}>
                          {TYPE_LABEL[m.movement_type] || m.movement_type}
                        </Badge>
                        <span className="font-medium text-foreground">{m.quantity}</span>
                        <span className="text-muted-foreground break-words flex-1 min-w-0">{m.reference || '—'}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(m.created_at).toLocaleString(dateLocale(i18n.language))}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {confirmZero && (
              <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4">
                <p className="text-sm text-foreground mb-3">
                  {t('cat_inventory.zeroConfirm')}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setConfirmZero(false)}>{t('cat_common.no')}</Button>
                  <Button variant="destructive" size="sm" disabled={saving} onClick={() => onZeroOut?.()}>
                    {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null} {t('cat_inventory.zeroYes')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="shrink-0 mt-0 px-6 py-4 border-t bg-card flex flex-row flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {isExisting && onZeroOut && (
                <Button variant="destructive" onClick={() => setConfirmZero(true)} disabled={saving}>
                  {t('cat_inventory.zeroOut')}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>{t('cat_common.cancel')}</Button>
              <Button
                disabled={!canSave || saving}
                onClick={() => onSubmit({
                  product_id: activeProduct.id,
                  movement_type: movementType,
                  quantity: qtyNum,
                  reference: reference || undefined,
                })}
              >
                {saving ? <><Loader2 size={14} className="animate-spin mr-1" /> {t('cat_common.saving')}</> : t('cat_common.save')}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
